/**
 * Deal file — the full editable record behind a deal.
 *
 * Deliberately does NOT route through `toCamelCase`. That helper rewrites the
 * keys of every nested object, and three payloads here are keyed by catalogue
 * field name rather than by a fixed schema:
 *
 *   - `custom_fields` on the loan            e.g. { cap_rate: 5.75 }
 *   - `derivation.derived` / `.notes`        e.g. { debt_yield: 8.21 }
 *   - `derivation.conflicts`                 e.g. { dscr: {...} }
 *
 * Camelising those turns `cap_rate` into `capRate`, which no longer matches the
 * `name` the catalogue hands back, so the value renders under no field and the
 * next save writes a second, duplicate key. Read and write them verbatim.
 *
 * Native columns are addressed by their real column name (`property_name`,
 * `loan_amount`, …). LoanUpdate sets `populate_by_name`, so the snake_case name
 * is accepted alongside its camelCase alias — one spelling for both directions.
 */

import api from './api'
import { dateInputValue } from '@/utils/dates'

export type DealFieldKind = 'text' | 'currency' | 'percent' | 'number' | 'date' | 'select'

/** One entry in the server-side field catalogue (`GET /loans/field-definitions`). */
export type DealFieldDef = {
  /** Catalogue key. Must round-trip byte-for-byte — see the note above. */
  name: string
  label: string
  group: string
  kind: DealFieldKind
  /** True when the field is a real column; false means it rides in custom_fields. */
  native: boolean
  unit: string | null
  options: string[]
  /** Some rule can compute this field. */
  derivable: boolean
  /** This field feeds at least one rule — Randy's "accelerator" fields. */
  accelerator: boolean
  required?: boolean
  help?: string | null
}

export type DealFieldExtensionCreate = {
  name: string
  label: string
  group?: string
  kind: DealFieldKind
  options?: string[]
  unit?: string | null
  help?: string | null
}

export type DealDerivationRule = {
  target: string
  inputs: string[]
  note: string
}

export type DealFieldCatalogue = {
  fields: DealFieldDef[]
  rules: DealDerivationRule[]
  /** Parser key -> the catalogue field it fills, where the names differ
   *  (`term` -> `term_months`). Names the intake Field Mapping rows. */
  intake_aliases?: Record<string, string>
  /** Catalogue field -> the everyday names a lender uses for it ('loan to
   *  value' for `ltv`): the words ⌘K and the DB console resolve on the server,
   *  served so the Audit search reads the same list. */
  plain_english?: Record<string, string[]>
}

/** What a save reports back about fields it moved, and disagreements it found. */
export type DealDerivation = {
  derived: Record<string, unknown>
  notes: Record<string, string>
  conflicts: Record<string, { stated?: unknown; implied?: unknown; computed?: unknown; note?: string }>
}

/** The loan row as the API serialises it: snake_case columns, raw custom_fields. */
/** One figure the sponsor's own registered model states, at a named cell.
 *
 *  Never merged into the deal's fields. The sponsor asserting a stabilized NOI
 *  is not the client concluding one, and the comparison the platform exists to
 *  run depends on the two staying distinguishable.
 */
export type SponsorFigure = {
  value: number
  label: string | null
  filename: string
  sheet: string
  cell: string
  version_id: string
  version_number: number
  /** A formula cell — the model's own conclusion rather than a typed input. */
  computed: boolean
  /** Other cells in the same model that state this column differently. A
   *  workbook holding an underwritten in-place NOI on its summary and a
   *  trailing-twelve actual on its T-12 tab is stating two things, and neither
   *  is wrong. */
  alternatives?: SponsorFigure[]
}

export type RawDealFile = Record<string, unknown> & {
  id: string
  custom_fields?: Record<string, unknown> | null
  /** What the sponsor's model states, by catalogue field name. Attached by
   *  GET /loans/{id} only — the list endpoint does not carry it. */
  sponsor_model?: Record<string, SponsorFigure> | null
}

/** The sponsor's figure for one catalogue field, if their model states it. */
export function sponsorFigure(
  raw: RawDealFile | undefined | null,
  name: string,
): SponsorFigure | undefined {
  const figure = raw?.sponsor_model?.[name]
  return figure && typeof figure.value === 'number' ? figure : undefined
}

/**
 * Is the value this field holds the sponsor's figure?
 *
 * Not a plain equality, because the two are not always on the same scale. The
 * server reports a percent on the schema's 0-100 (70.0 for an LTV) while a
 * NATIVE percent column stores a fraction (`loans.ltv` holds 0.7) — the same
 * split `formatFieldValue` handles below. Compared naively, a field holding
 * exactly the sponsor's LTV reads as one the deal overrode.
 *
 * Compared rather than flagged on the server so an edit drops the mark in the
 * same keystroke: a badge that outlived the value it described would attribute
 * the client's own correction to the sponsor.
 */
export function holdsSponsorFigure(
  def: Pick<DealFieldDef, 'native' | 'kind'>,
  value: unknown,
  figure: SponsorFigure | undefined,
): boolean {
  if (!figure || value === null || value === undefined || value === '') return false
  const held = Number(value)
  if (!Number.isFinite(held)) return false
  const stated =
    def.native && def.kind === 'percent' ? figure.value / 100 : figure.value
  // Tolerance for the rounding the fraction conversion introduces, not for
  // genuinely different numbers: 0.0001 is well inside one basis point.
  return Math.abs(held - stated) < 1e-4
}

export type DealFileSaveResult = RawDealFile & { derivation?: DealDerivation }

/** Patch shape sent to PUT /loans/{id}: native columns at the top level, every
 *  catalogue field without a column nested under custom_fields. */
export type DealFilePatch = Record<string, unknown> & {
  custom_fields?: Record<string, unknown>
}

const EMPTY_DERIVATION: DealDerivation = { derived: {}, notes: {}, conflicts: {} }

export const dealFileService = {
  /** The field catalogue and derivation rules. Safe to camelise in principle —
   *  its keys are a fixed schema — but kept raw so `name` and `rules[].target`
   *  are handled identically everywhere in this module. */
  getCatalogue: () =>
    api.get('/loans/field-definitions').then((r) => r.data as DealFieldCatalogue),

  /** Super-admin only — appends a platform-wide catalogue extension. */
  addCatalogueField: (body: DealFieldExtensionCreate) =>
    api
      .post('/loans/field-definitions', body)
      .then((r) => r.data as { field: DealFieldDef; fields: DealFieldDef[] }),

  getValues: (id: string) =>
    api.get(`/loans/${id}`).then((r) => r.data as RawDealFile),

  /** `version` is the deal's version when the screen loaded it (see
   *  dealVersion). Sent as If-Match, the server refuses the save with 409 if
   *  someone else saved in between, instead of silently overwriting them.
   *  Required: the server refuses a save that names no version (428).
   *
   *  `changeReason` is the one-line rationale owner decision #8 requires when
   *  `patch` hand-types a catalogue FIGURE (see `patchNeedsChangeReason`);
   *  the server refuses the save (422) if one is needed and missing. Sent as
   *  `change_reason` — `LoanUpdate` also accepts the `changeReason` alias,
   *  but every other field in this module is written snake_case. */
  save: (id: string, patch: DealFilePatch, version: string | null, changeReason?: string | null) =>
    api
      .put(
        `/loans/${id}`,
        changeReason ? { ...patch, change_reason: changeReason } : patch,
        ifMatch(version),
      )
      .then((r) => r.data as DealFileSaveResult),
}

/** The deal's version token: its updated_at, or created_at before any edit. */
export function dealVersion(raw: Record<string, unknown> | null | undefined): string | null {
  const value = raw?.updated_at ?? raw?.updatedAt ?? raw?.created_at ?? raw?.createdAt
  return typeof value === 'string' && value ? value : null
}

/** Request options carrying If-Match, or nothing when the version is unknown. */
export function ifMatch(version?: string | null): { headers: Record<string, string> } | undefined {
  return version ? { headers: { 'If-Match': `"${version}"` } } : undefined
}

/** Current value of a catalogue field, wherever it actually lives. */
export function readFieldValue(raw: RawDealFile | undefined, def: DealFieldDef): unknown {
  if (!raw) return undefined
  if (def.native) return raw[def.name]
  const custom = raw.custom_fields
  if (!custom || typeof custom !== 'object') return undefined
  return (custom as Record<string, unknown>)[def.name]
}

/**
 * `loans.loan_amount` is NOT NULL, so 0 is how a deal records that no amount
 * was stated (Cranbury Distribution Center, S02). It is never a $0 request.
 */
function isUnstatedLoanAmount(name: string | undefined, value: unknown): boolean {
  if (name !== 'loan_amount' || value === null || value === undefined || value === '') return false
  return Number(value) === 0
}

/**
 * The value a catalogue row reads: the stored value, except the loan amount a
 * deal does not state, which reads as absent. The header strip already showed
 * "—" for it while the Audit catalogue read "$0 · Suggested — confirm it".
 */
export function shownFieldValue(raw: RawDealFile | undefined, def: DealFieldDef): unknown {
  const value = readFieldValue(raw, def)
  return isUnstatedLoanAmount(def.name, value) ? undefined : value
}

/** Group defs in catalogue order, preserving the order the server sent so the
 *  tabs follow the sequence an underwriter works in rather than alphabetical. */
export function groupFields(fields: DealFieldDef[]): Array<{ group: string; fields: DealFieldDef[] }> {
  const order: string[] = []
  const byGroup = new Map<string, DealFieldDef[]>()
  for (const def of fields) {
    if (!byGroup.has(def.group)) {
      byGroup.set(def.group, [])
      order.push(def.group)
    }
    byGroup.get(def.group)!.push(def)
  }
  return order.map((group) => ({ group, fields: byGroup.get(group)! }))
}

/**
 * Split a draft into the patch the API expects.
 *
 * Only fields whose value actually changed are included: a full-record PUT
 * would stamp a manual edit event on every field and bury the real change in
 * the audit trail. Blank input clears the field (null) rather than writing "".
 */
export function buildPatch(
  defs: DealFieldDef[],
  raw: RawDealFile | undefined,
  draft: Record<string, unknown>,
): DealFilePatch {
  const patch: DealFilePatch = {}
  const custom: Record<string, unknown> = {}

  for (const def of defs) {
    if (!(def.name in draft)) continue
    const next = normalizeValue(def, draft[def.name])
    if (valuesEqual(next, readFieldValue(raw, def))) continue
    if (def.native) patch[def.name] = next
    else custom[def.name] = next
  }

  if (Object.keys(custom).length > 0) patch.custom_fields = custom
  return patch
}

/** A percent held in a real loan column (`loans.ltv`), which stores a fraction. */
function isNativePercent(def: Pick<DealFieldDef, 'native' | 'kind'>): boolean {
  return def.native && def.kind === 'percent'
}

/** Multipliers for the shorthand a desk types after an amount. "MM" is the
 *  finance spelling of millions, and is read as millions, not thousands. */
const MONEY_SCALE: Record<string, number> = {
  '': 1,
  k: 1_000,
  m: 1_000_000,
  mm: 1_000_000,
  mn: 1_000_000,
  b: 1_000_000_000,
  bn: 1_000_000_000,
}
const MONEY_TEXT = /^(-)?\$?(-)?(\d[\d,]*(?:\.\d*)?|\.\d+)(k|mm|mn|m|bn|b)?$/i

/**
 * An amount as typed — "$130,329,000", "130.3M", "130.3MM", "750K" — in
 * dollars, or null when the text is not an amount.
 *
 * Only a suffix the person typed scales the figure. A bare number is dollars:
 * the size of an amount is never guessed from its magnitude.
 */
export function parseMoneyText(text: string): number | null {
  const match = MONEY_TEXT.exec(text.replace(/\s/g, ''))
  if (!match) return null
  const figure = Number(match[3].replace(/,/g, ''))
  if (!Number.isFinite(figure)) return null
  // To the cent: 130.3 x 1,000,000 is 130,300,000.00000001 in binary.
  const dollars = Math.round(figure * MONEY_SCALE[(match[4] ?? '').toLowerCase()] * 100) / 100
  return match[1] || match[2] ? -dollars : dollars
}

/**
 * A typed entry read the way Save stores it: an amount's shorthand in dollars,
 * a percent in the column's own scale (a fraction for `loans.ltv`). Exported so
 * a screen that reads an unsaved edit — the Sponsor vs lender comparison —
 * reads the same number Save will write, never a second parse of its own.
 */
export function normalizeValue(def: Pick<DealFieldDef, 'native' | 'kind'>, value: unknown): unknown {
  if (value === undefined || value === null) return null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '') return null
    if (def.kind === 'currency') {
      const dollars = parseMoneyText(trimmed)
      return dollars === null ? trimmed : dollars
    }
    if (def.kind === 'percent' || def.kind === 'number') {
      // Accept what people actually type: "5.75%", "1,200", a "1.25x" DSCR.
      const cleaned = trimmed.replace(/[$,%\s]/g, '').replace(/x$/i, '')
      const parsed = Number(cleaned)
      if (!Number.isFinite(parsed)) return trimmed
      if (isNativePercent(def)) {
        // The column stores a fraction; people type the percentage they read
        // ("60%" or "60"). Stripping the sign and sending 60 stored a 6,000%
        // LTV and re-derived the property value as loan / 60. Every percent
        // box takes percentage points, whatever the figure's size: "0.6" is
        // 0.6% here as in the cap rate box, and "1" is 1%, never 100%
        // (nd-062). A unit is never guessed from a magnitude.
        return parsed / 100
      }
      return parsed
    }
    return trimmed
  }
  return value
}

/** Catalogue numbers that hold a calendar year, written without a separator. */
const YEAR_FIELD = /(^|_)year(_|$)/

/**
 * The value an edit box shows for a field.
 *
 * A native percent column holds a fraction (0.58) while every read-only
 * surface reads it as a percentage (58.00%); the unlocked box showed the
 * fraction, so a person typing the number they could see stored 58x the
 * value. The box shows the percentage with its sign, and saving converts it
 * back. An amount shows as dollars with separators ($130,329,000, not
 * "130329000"), and a date as the calendar day the read-only dossier prints.
 *
 * `typing`: the box has focus, so a string is what the person is typing and
 * is shown exactly as typed — reformatting mid-keystroke fights the caret.
 * Once they leave it, the entry reads back the way it will be saved.
 */
export function editableValue(
  def: (Pick<DealFieldDef, 'native' | 'kind'> & { name?: string; unit?: string | null }) | undefined,
  value: unknown,
  { typing = false }: { typing?: boolean } = {},
): string {
  if (value === null || value === undefined) return ''
  if (!def) return String(value)
  if (def.kind === 'date') return dateInputValue(value) ?? String(value)
  let figure: unknown = value
  if (typeof value === 'string') {
    if (typing) return value
    figure = normalizeValue(def, value)
  }
  if (typeof figure !== 'number' || !Number.isFinite(figure)) return String(value)
  // The loan amount a deal does not state: the read view shows "—"; the box
  // is empty, never "$0".
  if (isUnstatedLoanAmount(def.name, figure)) return ''
  switch (def.kind) {
    case 'currency': {
      // A loan size is NOT NULL and stored as 0 when the package states none;
      // the locked dossier reads that "—", so the edit box is empty too
      // rather than offering "$0" as a value a save could write back.
      if (figure === 0 && typeof value === 'number' && def.name && LOAN_SIZE_FIELDS.has(def.name)) return ''
      const places = Number.isInteger(figure) ? 0 : 2
      const money = Math.abs(figure).toLocaleString('en-US', {
        minimumFractionDigits: places,
        maximumFractionDigits: places,
      })
      return figure < 0 ? `-$${money}` : `$${money}`
    }
    case 'percent': {
      // A native percent column holds a fraction: always shown as points.
      const points = isNativePercent(def) ? figure * 100 : figure
      return `${Number(points.toFixed(4))}%`
    }
    case 'number': {
      if (def.unit === 'x') return `${Number(figure.toFixed(4))}x`
      if (def.name && YEAR_FIELD.test(def.name)) return String(figure)
      return figure.toLocaleString('en-US', { maximumFractionDigits: 4 })
    }
    default:
      return String(value)
  }
}

function percentRangeError(def: DealFieldDef, fraction: number): string | null {
  if (fraction >= 0 && fraction <= 1) return null
  return `${def.label} must be between 0% and 100% (entered ${Number((fraction * 100).toFixed(2))}%).`
}

/** Loan sizes, which are entered in dollars. */
const LOAN_SIZE_FIELDS = new Set(['loan_amount', 'initial_funding', 'fully_funded_amount'])
export const MIN_LOAN_DOLLARS = 100_000

/**
 * A loan size typed in millions ("45") without the zeros.
 *
 * Stored as $45, the Pipeline showed "$45" while the report — which reads
 * anything under 10,000 as millions — showed "$45.0MM": one deal, two sizes.
 * No loan this desk writes is under $100,000, so such an entry is refused
 * with the unit stated rather than stored ambiguously.
 */
export function loanAmountError(label: string, dollars: number): string | null {
  if (!(dollars > 0) || dollars >= MIN_LOAN_DOLLARS) return null
  const shown = Number(dollars.toFixed(2)).toLocaleString('en-US')
  const asMillions = Math.round(dollars * 1_000_000).toLocaleString('en-US')
  return `${label} is in dollars: $${shown} is below $${MIN_LOAN_DOLLARS.toLocaleString('en-US')}. For $${shown} million, enter ${asMillions}.`
}

function valueError(def: DealFieldDef, value: number): string | null {
  if (isNativePercent(def)) return percentRangeError(def, value)
  if (LOAN_SIZE_FIELDS.has(def.name)) return loanAmountError(def.label, value)
  return null
}

const NUMERIC_KINDS = new Set<DealFieldKind>(['currency', 'percent', 'number'])

/** A numeric field holding text that is not a number. Sent as-is, the server
 *  refused the whole save with no word on which field or why. */
function textError(def: DealFieldDef, text: string): string {
  const what = def.kind === 'currency' ? 'an amount' : def.kind === 'percent' ? 'a percentage' : 'a number'
  return `${def.label}: "${text}" is not ${what}.`
}

/** Why a typed value cannot be saved, or null when it can. */
export function fieldValueError(def: DealFieldDef, value: unknown): string | null {
  const next = normalizeValue(def, value)
  if (typeof next === 'string' && NUMERIC_KINDS.has(def.kind)) return textError(def, next)
  return typeof next === 'number' ? valueError(def, next) : null
}

/** Values in a patch the record cannot hold: an LTV above 100%, a loan in
 *  unstated millions, text in a number field. */
export function patchErrors(defs: DealFieldDef[], patch: DealFilePatch): string[] {
  const errors: string[] = []
  for (const def of defs) {
    const value = def.native ? patch[def.name] : patch.custom_fields?.[def.name]
    if (typeof value === 'string' && NUMERIC_KINDS.has(def.kind)) {
      errors.push(textError(def, value))
      continue
    }
    if (typeof value !== 'number') continue
    const error = valueError(def, value)
    if (error) errors.push(error)
  }
  return errors
}

/** Catalogue kinds that are a FIGURE — an amount, a rate, a ratio — as
 *  opposed to a name, a date or a workflow choice (stage, priority). Mirrors
 *  the server's NUMERIC_KINDS (`app/underwriting/dealfields.py`). */
const FIGURE_KINDS = new Set<DealFieldKind>(['currency', 'percent', 'number'])

/**
 * Whether this patch hand-types a catalogue FIGURE to a new value — the save
 * then needs the one-line reason owner decision #8 requires (nd3-05): typing
 * a new NOI, cap rate or loan amount over what the deal holds saves it
 * Confirmed, and Confirmed promises a rationale. A select (stage, priority),
 * a name or a date is a decision or a correction, not a "figure edit" the
 * rule names, so those need no reason.
 *
 * `patch` only carries a key when `buildPatch` already found it differs from
 * the deal's current value, so presence here already means "changed" —
 * mirrors the server's own diff in `_catalogue_figure_changes`.
 */
export function patchNeedsChangeReason(defs: DealFieldDef[], patch: DealFilePatch): boolean {
  return defs.some((def) => {
    if (!FIGURE_KINDS.has(def.kind)) return false
    return def.native ? def.name in patch : Boolean(patch.custom_fields && def.name in patch.custom_fields)
  })
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if ((a === null || a === undefined) && (b === null || b === undefined)) return true
  if (typeof a === 'number' && typeof b === 'number') return Math.abs(a - b) < 1e-9
  // The API returns numerics as numbers but a freshly typed value may be a
  // string; compare on value, not representation, so re-saving an untouched
  // form writes nothing.
  if (a !== null && b !== null && a !== undefined && b !== undefined) {
    const na = Number(a)
    const nb = Number(b)
    if (Number.isFinite(na) && Number.isFinite(nb) && `${a}`.trim() !== '' && `${b}`.trim() !== '') {
      return Math.abs(na - nb) < 1e-9
    }
    return `${a}` === `${b}`
  }
  return false
}

/**
 * Build a POST /loans payload from catalogue draft values.
 * Native columns at the top level (with LoanCreate aliases), the rest in custom_fields.
 * Composes `location` from city + state when location itself is blank.
 */
export function buildCreatePayload(
  defs: DealFieldDef[],
  draft: Record<string, unknown>,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {}
  const custom: Record<string, unknown> = {}

  for (const def of defs) {
    if (!(def.name in draft)) continue
    const next = normalizeValue(def, draft[def.name])
    if (next === null || next === undefined || next === '') continue
    if (def.native) payload[def.name] = next
    else custom[def.name] = next
  }

  if (!payload.location) {
    const city = typeof custom.city === 'string' ? custom.city : ''
    const state = typeof custom.state === 'string' ? custom.state : ''
    const composed = [city, state].filter(Boolean).join(', ')
    if (composed) payload.location = composed
  }

  if (Object.keys(custom).length > 0) payload.custom_fields = custom
  return payload
}

export function derivationOf(result: DealFileSaveResult | undefined): DealDerivation {
  const d = result?.derivation
  if (!d) return EMPTY_DERIVATION
  return {
    derived: d.derived ?? {},
    notes: d.notes ?? {},
    conflicts: d.conflicts ?? {},
  }
}

